import React, { useEffect, useState } from 'react';
import { getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function BlogManagement() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    image_url: '',
    category: '',
    author: 'İstanbul Turda Ekibi',
    is_featured: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await getBlogPosts();
      setPosts(response.data);
    } catch (error) {
      toast.error('Blog yazıları yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await updateBlogPost(editingPost.id, formData);
        toast.success('Blog yazısı güncellendi');
      } else {
        await createBlogPost(formData);
        toast.success('Blog yazısı eklendi');
      }
      setIsModalOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error('İşlem başarısız');
      console.error(error);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt,
      image_url: post.image_url,
      category: post.category,
      author: post.author,
      is_featured: post.is_featured
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteBlogPost(id);
      toast.success('Blog yazısı silindi');
      fetchPosts();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      image_url: '',
      category: '',
      author: 'İstanbul Turda Ekibi',
      is_featured: false
    });
  };

  if (loading) return <div className="text-center py-12">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Blog Yönetimi</h2>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-blog-btn">
              <Plus size={20} className="mr-2" /> Yeni Yazı Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Blog Yazısı Düzenle' : 'Yeni Blog Yazısı'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="blog-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Başlık *</Label>
                  <Input 
                    name="title" 
                    value={formData.title} 
                    onChange={handleTitleChange} 
                    required 
                    data-testid="blog-title-input"
                  />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input 
                    name="slug" 
                    value={formData.slug} 
                    onChange={handleInputChange} 
                    required 
                    data-testid="blog-slug-input"
                  />
                </div>
                <div>
                  <Label>Kategori *</Label>
                  <Input 
                    name="category" 
                    value={formData.category} 
                    onChange={handleInputChange} 
                    placeholder="Ör: Tarih, Kültür" 
                    required 
                    data-testid="blog-category-input"
                  />
                </div>
                <div>
                  <Label>Yazar</Label>
                  <Input 
                    name="author" 
                    value={formData.author} 
                    onChange={handleInputChange} 
                    data-testid="blog-author-input"
                  />
                </div>
              </div>
              <div>
                <Label>Özet *</Label>
                <Textarea 
                  name="excerpt" 
                  value={formData.excerpt} 
                  onChange={handleInputChange} 
                  rows={2} 
                  required 
                  data-testid="blog-excerpt-input"
                />
              </div>
              <div>
                <Label>İçerik *</Label>
                <Textarea 
                  name="content" 
                  value={formData.content} 
                  onChange={handleInputChange} 
                  rows={12} 
                  required 
                  data-testid="blog-content-input"
                />
              </div>
              <div>
                <Label>Görsel URL *</Label>
                <Input 
                  name="image_url" 
                  value={formData.image_url} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="blog-image-input"
                />
                {formData.image_url && (
                  <div className="mt-2">
                    <img src={formData.image_url} alt="Önizleme" className="h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  name="is_featured" 
                  checked={formData.is_featured} 
                  onChange={handleInputChange} 
                  className="w-4 h-4"
                  data-testid="blog-featured-checkbox"
                />
                <Label>Öne Çıkan Yazı</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="blog-submit-btn">
                  {editingPost ? 'Güncelle' : 'Yayınla'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            Henüz blog yazısı bulunmuyor. Yeni bir yazı ekleyin!
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`blog-card-${post.id}`}>
              <img src={post.image_url} alt={post.title} className="w-full h-48 object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">{post.category}</span>
                  {post.is_featured && <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">Öne Çıkan</span>}
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.excerpt}</p>
                <p className="text-sm text-gray-500 mb-4">{post.author}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleEdit(post)} className="flex-1 bg-blue-500 hover:bg-blue-600" data-testid={`edit-blog-${post.id}`}>
                    <Edit size={16} className="mr-1" /> Düzenle
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(post.id)} className="flex-1" data-testid={`delete-blog-${post.id}`}>
                    <Trash2 size={16} className="mr-1" /> Sil
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
